import React, { useEffect, useState } from "react";
import axios from "axios";
import { Modal, Button, Form } from "react-bootstrap";
import { useTheme } from "../components/ThemeContext";

// Mirrors EmailEditorModal.js's create/edit flow, request shape, and
// success/error handling 1:1. The only real differences are content-level:
// no `subject` field, and `message` is a plain textarea instead of a
// CKEditor rich-text field, since WhatsApp templates are plain text
// (WhatsApp itself only supports lightweight *bold*/_italic_ markup, not
// HTML).
const WhatsappTemplateEditorModal = ({
  show,
  onClose,
  mode = "create",
  initialTemplate = null,
  onSaved,
}) => {
  const [templateId, setTemplateId] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [course, setCourse] = useState("");
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [kept, setKept] = useState([]);

  const { isDark } = useTheme();

  const parseAttachments = (tpl) => {
    try {
      const arr = JSON.parse(tpl?.attachments || "[]");
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  };

  useEffect(() => {
    if (mode === "edit" && initialTemplate) {
      setTemplateId(initialTemplate.template_id ?? initialTemplate.id ?? null);
      setTemplateName(initialTemplate.template_name || "");
      setCourse(initialTemplate.course || "");
      setMessage(initialTemplate.message || "");
      const files = parseAttachments(initialTemplate);
      setExistingFiles(files);
      setKept(files);
      setAttachments([]);
    } else if (mode === "create") {
      clearForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, initialTemplate, show]);

  const clearForm = () => {
    setTemplateId(null);
    setTemplateName("");
    setCourse("");
    setMessage("");
    setAttachments([]);
    setExistingFiles([]);
    setKept([]);
  };

  const handleFileChange = (e) => {
    setAttachments(Array.from(e.target.files));
  };

  const toggleKeep = (file) => {
    setKept((prev) =>
      prev.includes(file) ? prev.filter((f) => f !== file) : [...prev, file]
    );
  };

  const handleSubmit = async () => {
    if (!templateName || !course || !message) {
      alert("Please fill in template name, course, and message.");
      return;
    }

    if (mode === "create") {
      const formData = new FormData();
      formData.append("templateName", templateName);
      formData.append("course", course);
      formData.append("message", message);
      formData.append("iscopy", "0");
      attachments.forEach((file) => formData.append("attachments", file));

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API}/saveWhatsappTemplate`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        if (response.data.success) {
          alert("WhatsApp Template Saved Successfully!");
          clearForm();
          onSaved?.();
          onClose();
        } else {
          alert(response.data.message || "Failed to save WhatsApp template.");
        }
      } catch (error) {
        console.error("Error saving WhatsApp template:", error);
        alert("Server error. Please try again later.");
      }
      return;
    }

    // EDIT mode
    const formData = new FormData();
    formData.append("template_id", templateId);
    formData.append("templateName", templateName);
    formData.append("course", course);
    formData.append("message", message);
    formData.append("keepAttachments", JSON.stringify(kept));
    attachments.forEach((file) => formData.append("attachments", file));

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}/updateWhatsappTemplate`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (response.data.success) {
        alert("WhatsApp Template Updated Successfully!");
        onSaved?.();
        onClose();
      } else {
        alert(response.data.message || "Failed to update WhatsApp template.");
      }
    } catch (error) {
      console.error("Error updating WhatsApp template:", error);
      alert("Server error. Please try again later.");
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      className={isDark ? "text-white" : ""}
    >
      <Modal.Header
        className={isDark ? "bg-dark text-white border-secondary" : ""}
        closeButton
      >
        <Modal.Title>
          {mode === "edit" ? "Edit WhatsApp Template" : "Create WhatsApp Template"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={isDark ? "bg-dark text-white" : ""}>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label className={isDark ? "text-white" : ""}>
              WhatsApp Template Name
            </Form.Label>
            <Form.Control
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Enter WhatsApp template name"
              className={
                isDark
                  ? "bg-dark text-white border-secondary dark-placeholder"
                  : ""
              }
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className={isDark ? "text-white" : ""}>
              Course
            </Form.Label>
            <Form.Control
              type="text"
              value={course}
              onChange={(e) => setCourse(e.target.value)}
              placeholder="Enter course"
              className={
                isDark
                  ? "bg-dark text-white border-secondary dark-placeholder"
                  : ""
              }
            />
          </Form.Group>

          {mode === "edit" && existingFiles.length > 0 && (
            <Form.Group className="mb-3">
              <Form.Label className={isDark ? "text-white" : ""}>
                Existing Attachments
              </Form.Label>
              <div className="border rounded p-2">
                {existingFiles.map((f, idx) => (
                  <Form.Check
                    key={idx}
                    type="checkbox"
                    label={f}
                    checked={kept.includes(f)}
                    onChange={() => toggleKeep(f)}
                    className={isDark ? "text-white" : ""}
                  />
                ))}
              </div>
              <small className="text-secondary d-block mt-1">
                Uncheck a file to remove it from this template.
              </small>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label className={isDark ? "text-white" : ""}>
              {mode === "edit" ? "Add New Attachments" : "Attachments"}
            </Form.Label>
            <Form.Control
              type="file"
              multiple
              onChange={handleFileChange}
              className={isDark ? "bg-dark text-white border-secondary" : ""}
            />
            {attachments.length > 0 && (
              <ul className="mt-2">
                {attachments.map((file, index) => (
                  <li key={index} className="text-muted small">
                    {file.name}
                  </li>
                ))}
              </ul>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className={isDark ? "text-white" : ""}>
              Message
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={8}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter WhatsApp message text (supports WhatsApp's own *bold*/_italic_ markup)"
              className={
                isDark
                  ? "bg-dark text-white border-secondary dark-placeholder"
                  : ""
              }
            />
          </Form.Group>

          <small className="text-secondary">
            *** Please do not include greetings (e.g., "Dear User") or closings
            (e.g., "Best Regards") in the message
          </small>
        </Form>
      </Modal.Body>
      <Modal.Footer className={isDark ? "bg-dark border-secondary" : ""}>
        <Button variant={isDark ? "secondary" : "light"} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="outline-light"
          className="btn-color"
          onClick={handleSubmit}
        >
          {mode === "edit" ? "Update Template" : "Save WhatsApp Template"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WhatsappTemplateEditorModal;